/**
 * One envelope or bill: remaining first, then spent of available, then a meter.
 *
 * Title sits on its own row so a long name can wrap. Kind badges stay off that
 * line — they were winning the fight against the name.
 */

import {
  dueDateDescription,
  formatMoney,
  type BudgetItemBalance,
} from '@alohamula/domain';
import { colorVar } from '../budget/colors';
import { Badge, Dot, Meter, Money } from './ui';
import { PinIcon } from './icons';

interface BudgetItemCardProps {
  balance: BudgetItemBalance;
  depth?: number;
  onClick?: (balance: BudgetItemBalance) => void;
  onTogglePin?: () => void;
}

export function BudgetItemCard({
  balance,
  depth = 0,
  onClick,
  onTogglePin,
}: BudgetItemCardProps) {
  const { item, spent, remaining, available, progress, isOverBudget, isLowBalance, isEmpty } =
    balance;
  const status = isOverBudget ? 'over' : isLowBalance || isEmpty ? 'low' : 'normal';
  const accumulated = balance.carriedIn;

  const content = (
    <>
      <div className="item-card__title">
        <Dot color={colorVar(item.color)} />
        <span className="item-card__name">{item.name}</span>
        {item.isFund && (
          <span className="item-card__mark" title="Fund">
            Fund
          </span>
        )}
      </div>

      <div className="item-card__figures">
        <span className="item-card__remaining">
          <Money value={Math.abs(remaining)} tone={isOverBudget ? 'over' : 'inherit'} />
        </span>
        <span className="item-card__of">
          {isOverBudget ? `${formatMoney(Math.abs(remaining))} over` : 'remaining'}
        </span>
      </div>

      <p className="item-card__spent">
        {formatMoney(spent)} of {formatMoney(available)}
      </p>

      <Meter
        progress={progress}
        variant={item.kind === 'bill' ? 'bill' : 'envelope'}
        status={status}
        label={`${item.name}: ${formatMoney(spent)} spent of ${formatMoney(available)}, ${formatMoney(remaining)} remaining`}
      />

      {Math.abs(accumulated) > 0.005 && (
        <p className={`item-card__carry${accumulated < 0 ? ' item-card__carry--over' : ''}`}>
          {accumulated < 0
            ? `${formatMoney(Math.abs(accumulated))} overspent last paycheck`
            : `${formatMoney(accumulated)} accumulated`}
        </p>
      )}

      <div className="item-card__status">
        {isOverBudget && <Badge tone="danger">Overspent</Badge>}
        {!isOverBudget && isEmpty && <Badge tone="warning">Empty</Badge>}
        {!isOverBudget && !isEmpty && isLowBalance && <Badge tone="warning">Low</Badge>}
      </div>

      <div className="item-card__foot">
        <span>
          {balance.transactionCount === 0
            ? 'No charges yet'
            : `${balance.transactionCount} ${balance.transactionCount === 1 ? 'charge' : 'charges'}`}
        </span>
        {item.kind === 'bill' && item.dueDay != null && <span>{dueDateDescription(item.dueDay)}</span>}
      </div>
    </>
  );

  const nestedStyle = depth > 0 ? { marginLeft: 'var(--space-lg)' } : undefined;

  return (
    <div className="item-wrap" style={nestedStyle}>
      {onClick ? (
        <button type="button" className="item-card" onClick={() => onClick(balance)}>
          {content}
        </button>
      ) : (
        <div className="item-card">{content}</div>
      )}
      {onTogglePin && (
        <button
          type="button"
          className={`item-card__pin${item.isPinned ? ' item-card__pin--on' : ''}`}
          aria-pressed={Boolean(item.isPinned)}
          aria-label={
            item.isPinned
              ? `Unpin ${item.name} from Overview`
              : `Pin ${item.name} to Overview`
          }
          onClick={(event) => {
            event.stopPropagation();
            onTogglePin();
          }}
        >
          <PinIcon width={16} height={16} />
        </button>
      )}
    </div>
  );
}
